import { useEffect, useState } from "react";
import Link from "next/link";

import getStrain from "@/api/requests/getStrain";
import { Action, Category, Label } from "@/constants/events";
import { StrainPageProvider as StrainProvider } from "@/context/StrainPageContext";
import { StrainV2 } from "@/custom-types/StrainV2";
import { useEventTracker } from "@/hooks/useEventTracker";
import { useSelector } from "@/redux/hooks";
import { getDispensaryId } from "@/redux/selectors/cartDispensary";
import { getUserId } from "@/redux/selectors/user";
import { isRedirectHint } from "@/utils/redirectHints";

import Card from "@/components/botanic/Card/Card";
import Classification from "@/components/botanic/Classification/Classification";
import IconSvg from "@/components/IconSvg/IconSvg";
import MedicalDisclaimers from "@/components/MedicalDisclaimers";
import FavoriteButton from "@/components/StrainPage/FavoriteStrainButton";
import ReviewCTA from "@/components/StrainPage/ReviewCTA";
import StrainCannabinoids from "@/components/StrainPage/StrainCannabinoids";
import StrainHighlights from "@/components/StrainPage/StrainHighlights";
import StrainQuizContainer from "@/components/StrainQuiz/StrainQuizContainer";
import TrackImpression from "@/components/TrackImpression/TrackImpression";

const LeaflyNugKnowledge: React.FC<{
  strainSlug?: string;
  menuItemId: number;
}> = ({ strainSlug, menuItemId }) => {
  const { publishEvent } = useEventTracker();
  const dispensaryId = useSelector(getDispensaryId);
  const userId = useSelector(getUserId);
  const [strain, setStrain] = useState<StrainV2>();
  const isStockImage = !!strain?.stockNugImage;

  const handleEventTracking = (
    label: Label,
    category = Category.dispensaryStrainDetails,
  ) => {
    publishEvent({
      action: Action.click,
      category,
      dispensaryId,
      label,
      menuItemId,
      strainId: strain?.id,
      userId,
    });
  };

  useEffect(() => {
    if (strainSlug) {
      const retrieveStrain = async () => {
        const data = await getStrain(strainSlug);
        if (data && !isRedirectHint(data)) {
          setStrain(data);
        }
      };

      retrieveStrain();
    }
  }, [strainSlug]);

  return strain ? (
    <StrainProvider strain={strain}>
      <TrackImpression
        category={Category.dispensaryStrainDetails}
        label={Label.viewStrainDetailsModule}
        customDimensions={{
          dispensaryId,
          menuItemId,
          strainId: strain.id,
          userId,
        }}
      >
        <div className="flex flex-col gap-lg items-center justify-center pb-md pt-xxl">
          <div className="flex flex-col gap-3 items-center justify-center">
            <IconSvg width="36" height="36" filePath={"knowledge.svg"} />
            <h3 className="text-[20px] font-extrabold">Leafly nug knowledge</h3>
          </div>
          <MedicalDisclaimers
            className="w-full flex px-lg"
            disclaimerOverride="The info below is sourced from strain reviews and not specific to any product. It is not a substitute for professional medical advice."
          />
        </div>
        <Card className="grid md:grid-cols-2 grid-cols-1 items-stretch bg-white px-lg py-10 gap-xl">
          <div className="flex items-center flex-1">
            <div className="flex relative isolation flex-auto h-full justify-center items-center">
              <img
                alt={strain.name}
                src={strain.nugImage || undefined}
                className="object-contain z-0"
              />
              {isStockImage && (
                <div
                  data-testid="hero-image-stock-photo"
                  className="absolute -bottom-[22px] md:-bottom-2 left-0 leading-[1rem] text-center w-full p-xs text-grey text-xs"
                >
                  stock photo similar to {strain.name}
                </div>
              )}

              <div className="absolute bottom-0 left-0">
                <StrainQuizContainer showResults={true} inline />
              </div>
            </div>
            <div className="flex flex-col items-center justify-center flex-shrink gap-5 px-md whitespace-nowrap">
              <ReviewCTA
                showRedesign={true}
                starLink="#pdp-strain-reviews"
                strainDetailsEventTracking={() =>
                  handleEventTracking(Label.strainReview)
                }
              />
              <div className="flex justify-between">
                <FavoriteButton
                  eventTrackingProps={{
                    dispensaryId,
                    favoriteGaLabel: Label.favoriteDispensaryStrainDetails,
                    menuItemId,
                    strainId: strain.id,
                    unFavoriteGaLabel: Label.unfavoriteDispensaryStrainDetails,
                    userId,
                  }}
                />
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-md flex-none">
            <div className="flex flex-col gap-sm">
              <div className="flex">
                {strain.phenotype && (
                  <Classification variant="phenotype">
                    {strain.phenotype}
                  </Classification>
                )}
                <StrainCannabinoids />
              </div>
              <Link
                shallow
                className="heading--l mb-xs"
                onClick={() => handleEventTracking(Label.strainNameLink)}
                href={`/strains/${strain.slug}`}
              >
                {strain.name}
              </Link>
              {strain.subtitle && (
                <p
                  itemProp="name"
                  className="text-xs font-normal truncate text-secondary"
                >
                  {strain.subtitle}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-xl">
              <StrainHighlights showAllHighlights={false} />
              <div className="flex justify-center">
                <Link
                  shallow
                  className="underline text-green font-bold"
                  onClick={() =>
                    handleEventTracking(Label.learnMoreAboutStrainLink)
                  }
                  href={`/strains/${strain.slug}`}
                >{`Learn more about ${strain.name}`}</Link>
              </div>
            </div>
          </div>
        </Card>
      </TrackImpression>
    </StrainProvider>
  ) : (
    <></>
  );
};

export default LeaflyNugKnowledge;
